import { useQuery } from '@tanstack/react-query'
import { useSettingsStore } from '../stores/settingsStore'
import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchHistoricalCandles, type UpstoxCandle } from '../services/candle-service'

const GIFT_NIFTY_URL = 'https://service.upstox.com/api-orchestrator/open/v2/global-indices/GLOBAL_INDEX%7CSGX%20NIFTY/?additionalData=marketData'

export function useGiftNifty(niftyTimestamp: number) {
    const isLive = useSettingsStore(s => s.isLive)
    const historicalDate = useSettingsStore(s => s.historicalDate)
    const inspectionSource = useSettingsStore(s => s.inspectionSource)
    
    // LIVE FETCHING
    const { data: liveData } = useQuery({
        queryKey: ['gift-nifty-live'],
        queryFn: async () => {
            const res = await fetch(GIFT_NIFTY_URL)
            if (!res.ok) throw new Error('Failed to fetch Gift Nifty')
            const json = await res.json()
            return json?.data?.marketData?.ltpc?.ltp as number || 0
        },
        enabled: isLive && inspectionSource !== 'HistoricalSnapshot',
        refetchInterval: 2000,
        refetchIntervalInBackground: true,
        staleTime: 1000,
    })

    // HISTORICAL FETCHING
    const { data: historicalCandles } = useQuery({
        queryKey: ['gift-nifty-history', historicalDate],
        queryFn: async () => {
            if (!historicalDate) return []
            // Fetch 1minute candles for the historical date
            const candles = await fetchHistoricalCandles('GLOBAL_INDEX|SGX NIFTY', 1, '1minute', 'GLOBAL_INDEX|SGX NIFTY', historicalDate)
            return candles
        },
        enabled: !isLive && inspectionSource === 'HistoricalSnapshot' && !!historicalDate,
        staleTime: Infinity,
    })

    // SYNC HISTORICAL DATA TO NIFTY TIMESTAMP
    const [historicalLtp, setHistoricalLtp] = useState<number>(0)
    
    useEffect(() => {
        if (!isLive && inspectionSource === 'HistoricalSnapshot' && historicalCandles && historicalCandles.length > 0 && niftyTimestamp > 0) {
            // Find the closest candle to the niftyTimestamp
            let closestCandle: UpstoxCandle | null = null
            let minDiff = Infinity
            
            for (const candle of historicalCandles) {
                const cTime = new Date(candle.timestamp).getTime()
                const diff = Math.abs(cTime - niftyTimestamp)
                if (diff < minDiff) {
                    minDiff = diff
                    closestCandle = candle
                }
            }
            
            if (closestCandle) {
                setHistoricalLtp(closestCandle.close)
            }
        }
    }, [isLive, inspectionSource, historicalCandles, niftyTimestamp])

    const activeLtp = (isLive && inspectionSource !== 'HistoricalSnapshot') ? (liveData || 0) : historicalLtp

    return {
        giftNiftyLtp: activeLtp,
        isLoading: (!isLive && inspectionSource === 'HistoricalSnapshot' && (!historicalCandles || historicalCandles.length === 0)) || (isLive && !liveData),
    }
}
